/**
 * CumulativeKpiView — Phase 30 Wave 5 (Chunk E)
 *
 * Renders per-shift-window cumulative KPI roll-up for multi-trigger KPIs (e.g.
 * stand-up counts: 5×/day → 1 row per shift window). Replaces the per-trigger
 * chart when `[Cumulative]` is active on `/analytics/kpi`.
 *
 * Read-time tz contract (EM Item 7b): `shiftWindowStart` arrives as ISO UTC;
 * label formatting uses browser-local timezone. Template-level
 * `assessment_schedules.timezone` is NOT honored at read time — write-time
 * precedence already resolved the canonical shift window.
 */

import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Loader2, BarChart3 } from 'lucide-react';
import { useCumulativeKpi } from '../../../hooks/useKpi';
import type { CumulativeKpiBucket, KpiAggregation } from '@guild/shared';

interface CumulativeKpiViewProps {
  kpiCode: string;
  startDate: string;
  endDate: string;
  /** Feature 2 (G-34): one crew's windows (KPI Analytics' `?shift=`); absent = every crew. */
  shiftId?: string;
}

const STATUS_COLORS = {
  normal: '#10b981',
  warning: '#f59e0b',
  critical: '#ef4444',
  unknown: '#00674a',
};

function formatKpiValue(value: number | null, unit: string | null): string {
  if (value === null || value === undefined) return '--';
  if (unit === 'seconds') {
    const minutes = Math.floor(value / 60);
    const seconds = Math.round(value % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  if (unit === 'percent' || unit === '%') return `${value.toFixed(1)}%`;
  if (unit === 'count' || Number.isInteger(value)) return Math.round(value).toLocaleString();
  return value.toFixed(2);
}

function formatShiftWindowLabel(isoUtc: string, shiftName: string | null): string {
  const date = new Date(isoUtc);
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const shift = shiftName ?? 'Unattributed';
  return `${dateLabel} — ${shift}`;
}

function aggregationLabel(agg: KpiAggregation | null): string {
  switch (agg) {
    case 'sum':
      return 'Sum';
    case 'count':
      return 'Count';
    case 'average':
      return 'Average';
    case 'min':
      return 'Min';
    case 'max':
      return 'Max';
    case 'percentile_50':
      return 'Median';
    case 'percentile_95':
      return 'P95';
    default:
      return 'Value';
  }
}

interface ChartRow {
  label: string;
  /** null = no measured value, never coerced to 0 (G-35). */
  value: number | null;
  count: number;
  shiftWindowStart: string;
  shiftName: string | null;
  operatorId: string | null;
  thresholdStatus: 'normal' | 'warning' | 'critical' | null;
}

const BUCKET_OPTION: 'shift_window' = 'shift_window';

export function CumulativeKpiView({
  kpiCode,
  startDate,
  endDate,
  shiftId,
}: CumulativeKpiViewProps) {
  const { data, isLoading, isError } = useCumulativeKpi({
    kpiCode,
    startDate,
    endDate,
    bucket: BUCKET_OPTION,
    shiftId,
  });

  const chartData = useMemo<ChartRow[]>(() => {
    if (!data?.buckets) return [];
    return data.buckets
      .map((b: CumulativeKpiBucket) => {
        const baseLabel = formatShiftWindowLabel(b.shiftWindowStart, b.shiftName);
        return {
          label: baseLabel,
          value: b.cumulativeValue,
          count: b.measurementCount,
          shiftWindowStart: b.shiftWindowStart,
          shiftName: b.shiftName,
          operatorId: b.operatorId,
          thresholdStatus: b.thresholdStatus,
        };
      })
      .sort(
        (a, b) =>
          new Date(a.shiftWindowStart).getTime() - new Date(b.shiftWindowStart).getTime()
      );
  }, [data]);

  const unit = data?.kpi.unit ?? null;
  const aggregation = data?.kpi.aggregationType ?? null;

  const formatYAxis = (v: number) => formatKpiValue(v, unit);

  const CustomBarTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: ChartRow }>;
  }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="text-sm font-semibold text-gray-900">{row.label}</p>
        <div className="mt-2 space-y-1 text-xs text-gray-600">
          <div className="flex items-center justify-between gap-4">
            <span>{aggregationLabel(aggregation)}</span>
            <span className="font-bold text-gray-900 tabular-nums">
              {formatKpiValue(row.value, unit)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Measured runs</span>
            <span className="font-medium text-gray-700">{row.count}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Window start</span>
            <span className="font-mono text-[10px] text-gray-500">
              {new Date(row.shiftWindowStart).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
        <div>
          <h3 className="font-semibold text-gray-900">
            Cumulative {data?.kpi.name ?? 'KPI'} per shift window
          </h3>
          <p className="text-sm text-gray-500">
            Each bar combines the measured runs in one shift window. Windows with no measured
            run are not shown.
          </p>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : isError ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
              <BarChart3 className="h-7 w-7 text-red-400" />
            </div>
            <p className="mt-3 text-sm font-medium text-gray-900">
              Could not load cumulative roll-up
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Try a different KPI or date range.
            </p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
              <BarChart3 className="h-7 w-7 text-gray-400" />
            </div>
            <p className="mt-3 text-sm font-medium text-gray-900">
              No measured runs in this period
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Only runs with a measured value and a shift window appear here. Not-observed runs
              and runs with no shift window are left out.
            </p>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={formatYAxis}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomBarTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell
                      key={`cell-${idx}`}
                      fill={STATUS_COLORS[entry.thresholdStatus ?? 'unknown']}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Footer note — surface the V1 contract so reviewers don't expect cumulative-threshold colors yet */}
        <p className="mt-4 text-xs text-gray-400">
          Window labels use your browser timezone. Bars are not colored by threshold.
        </p>
      </div>
    </div>
  );
}

export default CumulativeKpiView;
